import React, { useEffect, useState } from 'react';
import TextInput from '../../pro/components/orderform/TextInput';
import quoteService from '../../../services/quote.service';
import type { LocationLookupRow } from '../fixtures';

interface Props {
  /** Current display name (the deliver-to / stop name). */
  value: string;
  /** Bound cms.locations code; > 0 shows the linked badge and suppresses search.
   *  Pass 0 on surfaces with no code column (stopoffs) — then it's a plain
   *  address-fill typeahead. */
  linkedCode: number;
  /** User typed — the caller should set the name and UNLINK (code → 0). */
  onType: (name: string) => void;
  /** User picked a result — the caller sets name + denormalized address (+ code). */
  onPick: (row: LocationLookupRow) => void;
  ariaLabel?: string;
  placeholder?: string;
}

/**
 * Debounced cms.locations typeahead shared by the edit surfaces (DIRECT
 * deliver-to, delivery lanes, stopoffs). Mirrors the create-wizard's delivery-
 * location picker (CreateQuoteWizard StepDelivery): lookup only — creation
 * lives on the locations page — and free text stays allowed, but a pick links
 * the location code so the denormalized address can be frozen (N18 / P10 #22).
 */
const LocationLookupField: React.FC<Props> = ({ value, linkedCode, onType, onPick, ariaLabel, placeholder }) => {
  const [results, setResults] = useState<LocationLookupRow[]>([]);
  const [searching, setSearching] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const q = (value ?? '').trim();
    if (q.length < 2) {
      setResults([]);
      setSearching(false);
      setOpen(false);
      return;
    }
    // Once a code is bound the field is "linked" — don't (re)open the dropdown.
    // Any keystroke unlinks via onType (caller clears the code), which re-enables
    // search on the next render.
    if (linkedCode > 0) {
      setSearching(false);
      setOpen(false);
      return;
    }
    setSearching(true);
    const t = setTimeout(async () => {
      try {
        const rows = await quoteService.lookupLocation(q);
        setResults(rows);
        setOpen(rows.length > 0);
      } catch {
        setResults([]);
      } finally {
        setSearching(false);
      }
    }, 300);
    return () => clearTimeout(t);
  }, [value, linkedCode]);

  return (
    <div className="relative flex-1 min-w-0">
      <TextInput
        value={value}
        onChange={onType}
        ariaLabel={ariaLabel}
        placeholder={placeholder ?? 'Search locations — min 2 characters…'}
      />
      {open && results.length > 0 && (
        <div className="absolute z-10 left-0 right-0 top-full mt-1 max-h-56 overflow-y-auto bg-white border border-slate-200 rounded-md shadow-lg dark:bg-slate-800 dark:border-slate-700">
          {results.map((row) => (
            <button
              key={row.locationcode}
              type="button"
              onClick={() => { setOpen(false); onPick(row); }}
              className="w-full text-left px-2.5 py-1.5 text-[0.75rem] hover:bg-blue-50 dark:hover:bg-slate-700 border-b border-slate-100 dark:border-slate-700 last:border-b-0"
            >
              <div className="font-semibold text-slate-800 dark:text-slate-100">{row.lname}</div>
              <div className="text-[0.6875rem] text-slate-500 dark:text-slate-400">
                {[row.city, row.state].filter(Boolean).join(', ')}
                <span className="ml-2 text-slate-400 dark:text-slate-500">#{row.locationcode}</span>
                {row.cyalias && <span className="ml-2">{row.cyalias}</span>}
                {row.locationtype && <span className="ml-2">{row.locationtype}</span>}
              </div>
            </button>
          ))}
        </div>
      )}
      {searching && (
        <div className="text-[0.6875rem] text-slate-400 dark:text-slate-500 mt-0.5">Searching…</div>
      )}
      {linkedCode > 0 && (
        <div className="text-[0.65625rem] text-slate-500 dark:text-slate-400 mt-0.5">
          Linked to location <b className="text-slate-700 dark:text-slate-200">#{linkedCode}</b>
        </div>
      )}
    </div>
  );
};

export default LocationLookupField;
